import axiosInstance from '../../../services/axiosConfig';
import { CWY_NETWORK } from '../config/cwyConfig';

export interface SavedWallet {
  wallet_id?: string;
  address: string;
  network: string;
  wallet_provider?: string;
  is_primary?: boolean;
  connected_at?: string;
  ownership_verified_at?: string;
  grant_redeemed_for_wallet?: boolean;
}

export type CwyGrantBlockReason = 'account_redeemed' | 'wallet_redeemed' | null;

export type CwyTransferRequestType = 'phase1_test_grant' | 'treasury_deposit';

export interface CwyTransferRequest {
  transfer_request_id: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected' | 'failed';
  request_type?: CwyTransferRequestType;
  amount_cwy: number;
  wallet_address: string;
  tx_signature?: string;
  rejection_reason?: string;
  tx_error?: string;
  reviewed_by_name?: string;
  createdAt?: string;
  tx_confirmed_at?: string;
  reviewed_at?: string;
}

export interface CwyDepositInfo {
  network: string;
  mint_address: string;
  treasury_wallet: string;
  linked_wallet_address: string | null;
}

export async function listWallets(): Promise<{
  wallets: SavedWallet[];
  primary: SavedWallet | null;
  grant_limits_enforced: boolean;
  soft_grant_limits: boolean;
}> {
  const { data } = await axiosInstance.get<{
    success: boolean;
    wallets: SavedWallet[];
    primary: SavedWallet | null;
    grant_limits_enforced?: boolean;
    soft_grant_limits?: boolean;
  }>('/api/client-auth/wallets');
  return {
    wallets: data.wallets ?? [],
    primary: data.primary ?? null,
    grant_limits_enforced: data.grant_limits_enforced ?? true,
    soft_grant_limits: data.soft_grant_limits ?? false,
  };
}

/** Primary saved wallet (legacy-friendly). */
export async function getSavedWallet(): Promise<SavedWallet | null> {
  const { data } = await axiosInstance.get<{
    success: boolean;
    wallet: SavedWallet | null;
    wallets?: SavedWallet[];
  }>('/api/client-auth/wallet');
  return data.wallet;
}

export async function linkWallet(params: {
  address: string;
  message: string;
  signature?: number[];
  signature_base58?: string;
  signed_message?: number[];
  wallet_provider: string;
  set_primary?: boolean;
}): Promise<{ wallet: SavedWallet; wallets: SavedWallet[]; warning?: string }> {
  const { data } = await axiosInstance.put<{
    success: boolean;
    wallet: SavedWallet;
    wallets: SavedWallet[];
    warning?: string;
  }>('/api/client-auth/wallets', {
    address: params.address,
    network: CWY_NETWORK,
    message: params.message,
    signature: params.signature,
    signature_base58: params.signature_base58,
    signed_message: params.signed_message,
    wallet_provider: params.wallet_provider,
    set_primary: params.set_primary,
  });
  return { wallet: data.wallet, wallets: data.wallets ?? [], warning: data.warning };
}

/** @deprecated Use linkWallet */
export async function saveWallet(params: {
  address: string;
  message: string;
  signature: number[];
  wallet_provider?: string;
}): Promise<{ wallet: SavedWallet; warning?: string }> {
  const result = await linkWallet({
    ...params,
    wallet_provider: params.wallet_provider ?? 'phantom',
  });
  return { wallet: result.wallet, warning: result.warning };
}

export async function setPrimaryWallet(params: {
  walletId: string;
  message: string;
  signature?: number[];
  signature_base58?: string;
  signed_message?: number[];
  wallet_provider: string;
}): Promise<SavedWallet[]> {
  const { data } = await axiosInstance.patch<{
    success: boolean;
    wallets: SavedWallet[];
  }>(`/api/client-auth/wallets/${params.walletId}/primary`, {
    message: params.message,
    signature: params.signature,
    signature_base58: params.signature_base58,
    signed_message: params.signed_message,
    wallet_provider: params.wallet_provider,
  });
  return data.wallets ?? [];
}

export async function removeWallet(walletId?: string): Promise<SavedWallet[]> {
  const url = walletId ? `/api/client-auth/wallets/${walletId}` : '/api/client-auth/wallet';
  const { data } = await axiosInstance.delete<{
    success: boolean;
    wallets: SavedWallet[];
  }>(url);
  return data.wallets ?? [];
}

export async function createTransferRequest(amount: number): Promise<CwyTransferRequest> {
  const { data } = await axiosInstance.post<{ success: boolean; transfer: CwyTransferRequest }>(
    '/api/cwy-transfers/request',
    { amount }
  );
  return data.transfer;
}

export async function getMyTransferRequests(): Promise<{
  items: CwyTransferRequest[];
  grant_redeemed: boolean;
  wallet_grant_redeemed: boolean;
  block_reason: CwyGrantBlockReason;
  primary_wallet_address: string | null;
  grant_limits_enforced: boolean;
  soft_grant_limits: boolean;
}> {
  const { data } = await axiosInstance.get<{
    success: boolean;
    items: CwyTransferRequest[];
    grant_redeemed?: boolean;
    wallet_grant_redeemed?: boolean;
    block_reason?: CwyGrantBlockReason;
    primary_wallet_address?: string | null;
    grant_limits_enforced?: boolean;
    soft_grant_limits?: boolean;
  }>('/api/cwy-transfers/me');
  const items = data.items ?? [];
  const grant_limits_enforced = data.grant_limits_enforced ?? true;
  const grant_redeemed =
    data.grant_redeemed ??
    items.some(
      (item) =>
        (item.request_type ?? 'phase1_test_grant') === 'phase1_test_grant' &&
        item.status === 'completed'
    );
  const wallet_grant_redeemed = grant_limits_enforced
    ? (data.wallet_grant_redeemed ?? false)
    : false;
  const block_reason = grant_limits_enforced
    ? (data.block_reason ??
        (grant_redeemed ? 'account_redeemed' : wallet_grant_redeemed ? 'wallet_redeemed' : null))
    : null;
  return {
    items,
    grant_redeemed,
    wallet_grant_redeemed,
    block_reason,
    primary_wallet_address: data.primary_wallet_address ?? null,
    grant_limits_enforced,
    soft_grant_limits: data.soft_grant_limits ?? !grant_limits_enforced,
  };
}

export async function getDepositInfo(): Promise<CwyDepositInfo> {
  const { data } = await axiosInstance.get<{
    success: boolean;
    network: string;
    mint_address: string;
    treasury_wallet: string;
    linked_wallet_address: string | null;
  }>('/api/cwy-transfers/deposit-info');
  return {
    network: data.network,
    mint_address: data.mint_address,
    treasury_wallet: data.treasury_wallet,
    linked_wallet_address: data.linked_wallet_address,
  };
}

export async function confirmTreasuryDeposit(params: {
  amount: number;
  tx_signature: string;
}): Promise<CwyTransferRequest> {
  const { data } = await axiosInstance.post<{ success: boolean; deposit: CwyTransferRequest }>(
    '/api/cwy-transfers/deposit',
    params
  );
  return data.deposit;
}
